import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import StringProperty from '../properties/StringProperty';
import NumberProperty from '../properties/NumberProperty';
import BoolProperty from '../properties/BoolProperty';
import ColorProperty from '../properties/ColorProperty';
import SliderProperty from '../properties/SliderProperty';
import DropdownProperty from '../properties/DropdownProperty';
import AssetProperty from '../properties/AssetProperty';
import CameraProperty from '../properties/CameraProperty';
import StepProperty from '../properties/StepProperty';
import IconBoolProperty from '../properties/IconBoolProperty';
import store, { StoreActions } from '../../state/store';
import TextProperty from '../properties/TextProperty';
import UIEditorValueChangeAction from '../../actions/ui/UIEditorValueChangeAction';
import config from '../../config';
import ColorAlphaProperty from '../properties/ColorAlphaProperty.tsx';
import IconSelectionProperty from '../properties/IconSelectionProperty';
import { AlignTextLeftIcon, AlignTextCenterIcon, AlignTextRightIcon, AnchorTopIcon, AnchorMiddleIcon, AnchorBottomIcon, PositionLeftIcon, PositionCenterIcon, PositionRightIcon, Anchor9CornerIcon, Anchor9SideIcon, Anchor9CenterIcon } from '../Iconset';
import { IconSelectionStyles, SyncStatuses, InsetTypes } from '../../globals';
import SyncedPropertyGroup from '../properties/SyncedPropertyGroup';
import Scenario from '../../classes/scenarioElements/Scenario';

function propertiesEqual(left, right)
{
	let equal = true;
	for (const key of Object.keys(left))
	{
		if ((!left && right) || (left && !right) || (left && right && !shallowEqual(left[key], right[key])))
		{
			equal = false;
		}
	}
	return equal; // the properties are not equal, we need to update (rerender)
}

export const UIPropertyTypes = Object.freeze({
	Group: 'group',
	Text: 'text',
	TextArea: 'textarea',
	Numeric: 'numeric',
	Toggle: 'toggle',
	ToggleIcon: 'toggle-icon',
	Image: 'image',
	Video: 'video',
	Audio: 'audio',
	Color: 'color',
	ColorAlpha: 'color-alpha',
	Range: 'range',
	Step: 'step',
	Url: 'url',
	Camera: 'camera',
	Anchor: 'anchor',
	InsetAnchor: 'inset-anchor',
	Align: 'align',
	AlignText: 'align-text',
	TextAreaPreset: 'text-area-preset',
	OptionLayout: 'option-layout',
	Size3: 'size3',
	FontSize: 'font-size',
	InsetType: 'inset-type',
});

export default function UIEditorComponentRenderer(props)
{
	const dispatch = useDispatch();

	const storedProperties = useSelector(state => props.slide ? state.uiModule.slideMaster : state.uiModule.ui, (left, right) =>
	{
		if (left[props.slide ?? props.step] && left[props.slide ?? props.step][props.template] && left[props.slide ?? props.step][props.template] &&
			right[props.slide ?? props.step] && right[props.slide ?? props.step][props.template] && right[props.slide ?? props.step][props.template])
		{
			const leftTemplate = left[props.slide ?? props.step][props.template];
			const rightTemplate = right[props.slide ?? props.step][props.template];

			if(Object.keys(leftTemplate).length === 0) 
			{
				return false;
			}

			for (let subcomponentKey of Object.keys(leftTemplate))
			{
				if (!propertiesEqual(leftTemplate[subcomponentKey], rightTemplate[subcomponentKey]))
				{
					return false;
				}
			}
		}
		return true;
	});

	const groupStates = useSelector(state => state.uiModule.groupState, shallowEqual);

	const [previousValue, setPreviousValue] = useState(null);

	const getProperty = (propertyData) =>
	{
		if (storedProperties[props.slide ?? props.step] &&
			storedProperties[props.slide ?? props.step][props.template] &&
			storedProperties[props.slide ?? props.step][props.template][propertyData.subcomponent])
		{
			return storedProperties[props.slide ?? props.step][props.template][propertyData.subcomponent][propertyData.key];
		}

		return null;
	}

	const getKeyExists = (propertyData) =>
	{
		return getProperty(propertyData) !== null && getProperty !== undefined;
	}

	const getPropertyValue = (propertyData) =>
	{
		const property = getProperty(propertyData);
		if (property)
		{
			return property.value;
		}
		return null;
	}

	const getPropertyChanged = (propertyData) =>
	{
		const property = getProperty(propertyData);
		if (property && property.changed && property.changed === true)
		{
			return true;
		}
		return false;
	}

	const getPropertyDefaultValue = (propertyData) =>
	{
		const property = getProperty(propertyData);
		if (property)
		{
			return property.default;
		}
		return null;
	}

	useEffect(() =>
	{
		// don't update a non-existent template
		if(!store.getState().uiModule.slideMaster[props.slide]) return;

		if (props.properties)
		{
			for (let propertyData of props.properties)
			{
				if (!getKeyExists(propertyData) && propertyData.subcomponent)
				{
					dispatch(StoreActions.ui.setProperty({
						step: props.step,
						slide: props.slide,
						template: props.template,
						subcomponent: propertyData.subcomponent,
						property: {
							name: propertyData.key,
							type: propertyData.type,
							value: propertyData.default !== undefined ? propertyData.default : '',
							default: propertyData.default !== undefined ? propertyData.default : null,
						}
					}))
				}
			}
		}
	}, []) // create the data the first time this object appears

	function onValueChanged(propertyData, value, changed = true)
	{
		if (previousValue === null)
		{
			setPreviousValue(getPropertyValue(propertyData))
		}

		//console.log("UI property " + propertyData.key + " of " + propertyData.subcomponent + " [" + propertyData.type + "] changed: " + value);

		const templateData = {
			step: props.step,
			slide: props.slide,
			breakSync: props.slide ? false : true,
			template: props.template,
			subcomponent: propertyData.subcomponent,
			property: {
				name: propertyData.key,
				type: propertyData.type,
				value: value,
				changed: !props.slide ? changed : false,
			}
		}

		dispatch(StoreActions.ui.setProperty(templateData));
	}

	function onValueCommitted(propertyData, value, changed = true)
	{
		//console.log("UI property " + propertyData.key + " of " + propertyData.subcomponent + " [" + propertyData.type + "] commited: " + value);

		const templateData = {
			step: props.step,
			slide: props.slide,
			breakSync: props.slide ? false : true,
			template: props.template,
			subcomponent: propertyData.subcomponent,
			property: {
				name: propertyData.key,
				type: propertyData.type,
				value: value,
				changed: !props.slide ? changed : false,
			}
		}

		new UIEditorValueChangeAction({ ...templateData }, previousValue !== null ? previousValue : getPropertyValue(propertyData));
		setPreviousValue(null);
	}

	function setGroupState(key, value)
	{
		const location = props.slide ?? props.step;
		dispatch(StoreActions.ui.setGroupState({ location: location, key: key, value: value }));
	}

	function getGroupState(key)
	{
		if (!groupStates) return null;

		const location = props.slide ?? props.step;
		if (!groupStates[location] || !groupStates[location][key]) return null;

		return groupStates[location][key];
	}

	function createPropertyComponent(property, decoratorMode = false)
	{
		const currentValue = getPropertyValue(property) ? getPropertyValue(property) : '';
		const commonProps = {
			key: property.key + '_' + property.title,
			label: property.title,
			className: property.className,
			value: currentValue,
			tooltip: property.tooltip,
			defaultValue: getPropertyDefaultValue(property) ? getPropertyDefaultValue(property) : "",
			onChangeCallback: ((v) =>
			{
				onValueChanged(property, v)
			}),
			onCommitCallback: ((v) =>
			{
				onValueCommitted(property, v)
			}),
			contextMenuAddons: [],
			contextMenuOverride: getPropertyChanged(property) ? [
				{
					renderObject: "Sync to template",
					id: "syncToTemplate",
					callback: () =>
					{
						onValueChanged(property, currentValue, false)
						onValueCommitted(property, currentValue, false);
					},
				},
			] : props.slide ? undefined : [],
			synced: true,
			showSyncStatus: getPropertyChanged(property),
			syncStatus: getPropertyChanged(property) ? SyncStatuses.OutOfSync : SyncStatuses.InSync,
			showResetButton: getPropertyChanged(property),
			resetFunc: () =>
			{
				onValueChanged(property, currentValue, false);
				onValueCommitted(property, currentValue, false);
			}
		};

		//hiding properties if they dependant property is not right for them
		if (property.dependingOnProperty && property.dependingOnValues)
		{
			let dependingPropertyValue = getPropertyValue(property.dependingOnProperty);
			if (!property.dependingOnValues.includes(dependingPropertyValue))
			{
				return null;
			}
		}

		switch (property.typeoverride ? property.typeoverride : property.type)
		{
			case UIPropertyTypes.Group:
				let children = [];
				for (let child of property.children)
				{
					children.push(createPropertyComponent(child));
				}

				let titleAddon = property.titleAddon ? getPropertyValue(property.titleAddon) : '';
				if (titleAddon && titleAddon.toLowerCase() !== property.title.toLowerCase())
				{
					titleAddon = ' (' + titleAddon + ')';
				}
				else
				{
					titleAddon = '';
				}

				const currentGroupState = getGroupState(property.key + '-group');

				return <SyncedPropertyGroup
					key={property.key + '-group'}
					title={property.title + titleAddon}
					tooltip={property.tooltip}
					minHeight={32}
					expanded={currentGroupState !== null ? currentGroupState : !property.collapsed}
					decorators={property.decorator ? createPropertyComponent(property.decorator, true) : undefined}
					locked={property.decorator ? (getPropertyValue(property.decorator) === false || getPropertyValue(property.decorator) === 'false' || getPropertyValue(property.decorator) === property.decorator.falseValue) ? true : false : false}
					onToggleCallback={(v) =>
					{
						setGroupState(property.key + '-group', v);
					}}
				>
					{children}
				</SyncedPropertyGroup>
			case UIPropertyTypes.Toggle:
				return <BoolProperty data-name={"ui-template-toggle-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} />
			case UIPropertyTypes.Step:
				return <StepProperty data-name={"ui-template-step-" + property.key + "-" + property.title} {...commonProps}
					warning={Scenario.getStep(getPropertyValue(property)) ? undefined : "The linked step doesn't exist"}
					value={getPropertyValue(property) ? getPropertyValue(property) : ''}
					onChangeCallback={undefined}
					onCommitCallback={(v) => onValueCommitted(property, v)}
				/>
			case UIPropertyTypes.Anchor:
				return <IconSelectionProperty data-name={"ui-template-anchor-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'top'}
					options={[
						{ display: <AnchorTopIcon />, value: "top" },
						{ display: <AnchorMiddleIcon />, value: "center" },
						{ display: <AnchorBottomIcon />, value: "bottom" },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.Image:
				return <AssetProperty data-name={"ui-template-image-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} type="image" />
			case UIPropertyTypes.ToggleIcon:
				return <IconBoolProperty data-name={"ui-template-toggle-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} decoratorMode={decoratorMode} falseValue={property.falseValue} trueValue={property.trueValue} />
			case UIPropertyTypes.Text:
				return <StringProperty data-name={"ui-template-text-" + property.key + "-" + property.title} {...commonProps} allowTextVariables={true} />
			case UIPropertyTypes.TextArea:
				return <TextProperty data-name={"ui-template-textarea-" + property.key + "-" + property.title} {...commonProps} rows={3} allowTextVariables={true} />
			case UIPropertyTypes.Range:
				return <SliderProperty data-name={"ui-template-range-" + property.key + "-" + property.title} {...commonProps}
					min={property.min ? property.min : 0}
					max={property.max ? property.max : 100}
					step={property.step ? property.step : 0.01}
					value={getPropertyValue(property) != undefined ? getPropertyValue(property) : property.default ? property.default : 0}
					onChangeCallback={(v) => onValueChanged(property, v)}
					onCommitCallback={(v) => onValueCommitted(property, v)}
				/>
			case UIPropertyTypes.Numeric:
				return <NumberProperty data-name={"ui-template-number-" + property.key + "-" + property.title} {...Object.assign({}, commonProps, {
					value: getPropertyValue(property) != undefined ? getPropertyValue(property) : 0
				})} />
			case UIPropertyTypes.FontSize:
				return <SliderProperty data-name={"ui-template-font-size-" + property.key + "-" + property.title} {...commonProps}
					min={12}
					max={48}
					step={2}
					value={getPropertyValue(property) != undefined ? getPropertyValue(property) : 14}
					field={true}
					onChangeCallback={(v) => onValueChanged(property, v)}
					onCommitCallback={(v) => onValueCommitted(property, v)}
				/>
			case UIPropertyTypes.Align:
				return <IconSelectionProperty data-name={"ui-template-anchor-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'left'}
					options={[
						{ display: <PositionLeftIcon />, value: "left" },
						{ display: <PositionCenterIcon />, value: "middle" },
						{ display: <PositionRightIcon />, value: "right" },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.AlignText:
				return <IconSelectionProperty data-name={"ui-template-anchor-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'left'}
					options={[
						{ display: <AlignTextLeftIcon />, value: "left" },
						{ display: <AlignTextCenterIcon />, value: "middle" },
						{ display: <AlignTextRightIcon />, value: "right" },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.OptionLayout:
				return <DropdownProperty data-name={"ui-template-option-layout-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'single column'}
					options={[
						{ display: "1 Column", value: "single column" },
						{ display: "2 Columns", value: "2 columns" },
						{ display: "3 Columns", value: "3 columns" },
						{ display: "4 Columns", value: "4 columns" },
						{ display: "5 Columns", value: "5 columns" },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.Color:
				return <ColorProperty data-name={"ui-template-color-" + property.key + "-" + property.title} {...Object.assign({}, commonProps, {
					value: getPropertyValue(property) ? getPropertyValue(property) : property.default ? property.default : '#ffffff',
				})} />
			case UIPropertyTypes.ColorAlpha:
				return <ColorAlphaProperty data-name={"ui-template-color-" + property.color.key + "-" + property.title}
					key={property.color.key}
					name={property.color.key}
					label={property.title}
					valueColor={getPropertyValue(property.color) ? getPropertyValue(property.color) : property.color.default ? property.color.default : '#ffffff'}
					valueAlpha={getPropertyValue(property.alpha) != undefined ? getPropertyValue(property.alpha) : property.alpha.default ? property.alpha.default : 0}
					postfixLabel={'%'}
					defaultValueColor={getPropertyDefaultValue(property.color) ? getPropertyDefaultValue(property.color) : ""}
					defaultValueAlpha={getPropertyDefaultValue(property.alpha) ? getPropertyDefaultValue(property.alpha) : ""}
					min={property.alpha.min ? property.alpha.min : 0}
					max={property.alpha.max ? property.alpha.max : 100}
					step={property.alpha.step ? property.alpha.step : 1}
					hideArrows={true}
					onChangeCallbackColor={(v) => onValueChanged(property.color, v)}
					onCommitCallbackColor={(v) => onValueCommitted(property.color, v)}
					onChangeCallbackAlpha={(v) => onValueChanged(property.alpha, v)}
					onCommitCallbackAlpha={(v) => onValueCommitted(property.alpha, v)}
					contextMenuAddonsColor={[]}
					contextMenuOverrideColor={getPropertyChanged(property.color) ? [
						{
							renderObject: "Sync to template",
							id: "syncToTemplate",
							callback: () =>
							{
								onValueChanged(property.color, currentValue, false)
								onValueCommitted(property.color, currentValue, false);
							},
						},
					] : props.slide ? undefined : []}
					contextMenuAddonsAlpha={[]}
					contextMenuOverrideAlpha={getPropertyChanged(property.alpha) ? [
						{
							renderObject: "Sync to template",
							id: "syncToTemplate",
							callback: () =>
							{
								onValueChanged(property.alpha, currentValue, false)
								onValueCommitted(property.alpha, currentValue, false);
							},
						},
					] : props.slide ? undefined : []}
					synced={true}
					showSyncStatus={(getPropertyChanged(property.alpha) || getPropertyChanged(property.color)) && config.SLIDEMASTER}
					syncStatus={(getPropertyChanged(property.alpha) || getPropertyChanged(property.color)) && config.SLIDEMASTER ? SyncStatuses.OutOfSync : SyncStatuses.InSync}
					showResetButton={(getPropertyChanged(property.alpha) || getPropertyChanged(property.color)) && config.SLIDEMASTER}
					resetFunc={() =>
					{
						onValueChanged(property.color, currentValue, false)
						onValueCommitted(property.color, currentValue, false);
						onValueChanged(property.alpha, currentValue, false)
						onValueCommitted(property.alpha, currentValue, false);
					}}
				/>
			case UIPropertyTypes.TextAreaPreset:
				return <DropdownProperty data-name={"ui-template-textarea-preset-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'top'}
					options={[
						{ display: "Headers Bottom", value: "headers-bottom" },
						{ display: "EULA", value: "eula" },
						{ display: "Headers Top", value: "headers-top" },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.InsetType:
				return <DropdownProperty data-name={"ui-template-inset-type-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'image'}
					options={[
						// { display: "Invisible", value: "invisible" },
						{ display: "Image", value: InsetTypes.IMAGE },
						{ display: "Step", value: InsetTypes.STEP },
						{ display: "Video", value: InsetTypes.VIDEO },
						{ display: "Audio", value: InsetTypes.AUDIO },
						{ display: "Url", value: InsetTypes.URL },
						{ display: "Camera", value: InsetTypes.CAMERA },
					]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.InsetAnchor:
				return <IconSelectionProperty data-name={"ui-template-inset-anchor-" + property.key + "-" + property.title} {...commonProps}
					selectionStyle={IconSelectionStyles.outline}
					value={getPropertyValue(property) ? getPropertyValue(property) : 'top-middle'}
					options={
						[
							[
								{ display: <Anchor9CornerIcon />, value: "top-left" },
								{ display: <Anchor9SideIcon />, value: "top-middle" },
								{ display: <Anchor9CornerIcon rotate={90} />, value: "top-right" },
							],
							[
								{ display: <Anchor9SideIcon rotate={-90} />, value: "middle-left" },
								{ display: <Anchor9CenterIcon />, value: "center" },
								{ display: <Anchor9SideIcon rotate={90} />, value: "middle-right" },
							],
							[
								{ display: <Anchor9CornerIcon rotate={-90} />, value: "bottom-left" },
								{ display: <Anchor9SideIcon rotate={180} />, value: "bottom-middle" },
								{ display: <Anchor9CornerIcon rotate={180} />, value: "bottom-right" },
							],
						]}
					onChangeCallback={undefined}
				/>
			case UIPropertyTypes.Audio:
				return <AssetProperty data-name={"ui-template-audio-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} type="audio" />
			case UIPropertyTypes.Video:
				return <AssetProperty data-name={"ui-template-video-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} type="video" />
			case UIPropertyTypes.Camera:
				return <CameraProperty data-name={"ui-template-camera-" + property.key + "-" + property.title} {...commonProps} onChangeCallback={undefined} contextMenuOverride={[]} />
			case UIPropertyTypes.Url:
				return <StringProperty data-name={"ui-template-url-" + property.key + "-" + property.title} {...commonProps}
					value={getPropertyValue(property) ? getPropertyValue(property) : ''}
					onChangeCallback={(v) => onValueChanged(property, v)}
					onCommitCallback={(v) => onValueCommitted(property, v)}
				/>
		}
	}

	function renderProperties()
	{
		let propertiesToRender = [];
		for (let property of props.properties)
		{
			propertiesToRender.push(createPropertyComponent(property));
		}

		return propertiesToRender;
	}

	return (
		renderProperties()
	)
}